import { Suspense, lazy, useState } from "react";
import { z } from "zod";

import type { ShopperCustomersTypes } from "@tsf/commerce-sdk-isomorphic";

import { OverlayBoundary } from "~/components/overlay-boundary";
import { Dialog } from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import { useCartSpend } from "~/hec/components/fsa-funds/use-cart-spend";
import { useAuth } from "~/hec/context/auth-context";
import {
  balanceEntrySchema,
  getCurrentTpaId,
  readCustomerCTPA,
} from "~/hec/helpers/tpa-branding-restore";
import { useOverlaysReady } from "~/hec/hooks/useOverlaysReady";
import { useTpaBalanceBasketSync } from "~/hec/tpa/use-tpa-balance-basket-sync";
import { cn } from "~/lib/utils";
import { zJson } from "~/lib/zod-helpers";
import { useRootLoaderData } from "~/root";
import { useTpaBalance } from "~/routes/resources/tpa-balance";
import { getCookieValue } from "~/utils/cookies";

import TpaTracker from "./tracker";
import type { BalanceData, BuilderImage, TrackerData } from "./types";

// COM-189 wave 4B: the balance-details modal is this graph's only path to the
// radix-tooltip stack and renders nothing while closed (Radix Dialog content),
// so it loads lazily — warmed on the overlays-ready signal, forced by open.
const BalanceModal = lazy(() => import("./balance-modal"));

const ACTIVE_CURRENCY = "USD";
const DEFAULT_EXPIRY_DAYS = 60;

// ASIFLEX shoppers are guests (no SLAS login), so the login gate would hide
// their balance. Cheap presence check of the (non-httpOnly) `asiflexData`
// cookie to allow the fetch; the loader does the authoritative parse + consent
// + enable-pref gating server-side (SFCC-11421).
function hasAsiflexCookie(): boolean {
  if (typeof document === "undefined") return false;
  return getCookieValue(document.cookie, "asiflexData") !== null;
}

/** `c_balanceResponse` entries by tpaId, each still unparsed; empty when unreadable. */
const balanceResponseSchema = z
  .preprocess(zJson, z.record(z.unknown()))
  .catch({});

// SFRA builds `popup` from every TPA's accounts in `c_balanceResponse`
// (balanceTracker.js getFormattedAccounts on origin/develop), skipping bad
// entries one by one, so the same count is known from loader data at SSR.
function cachedPlanCount(
  customer: ShopperCustomersTypes.Customer | null | undefined,
): number {
  return Object.values(
    balanceResponseSchema.parse(customer?.c_balanceResponse),
  ).reduce<number>(
    (n, entry) =>
      n + (balanceEntrySchema.safeParse(entry).data?.accounts?.length ?? 0),
    0,
  );
}

// SSR-side prediction of SFRA's `balanceTracker.active`: balanceDisplayData
// passes refresh=false and gates on these cached profile attrs (getBalance.js,
// balanceTracker.js, balance.js on origin/develop). TPAConfig flags aren't visible.
// TPA id: getCurrentTPA (profile.js:185-197 on origin/develop) = mostRecentTPA, then
// the deprecated mostRecentCardlessTPA. SFRA's `totalAvailableBalance !== false`
// is a presence check (objectUtils.js getNestedPropertyValue returns false when absent).
function expectsActiveTracker(
  customer: ShopperCustomersTypes.Customer | null | undefined,
): boolean {
  if (!customer?.c_isBalanceTrackerInitialized) return false;
  const cardless: unknown = customer.c_mostRecentCardlessTPA;
  const tpaId =
    getCurrentTpaId(readCustomerCTPA(customer)) ??
    (typeof cardless === "string" && cardless ? cardless : null);
  const entries = balanceResponseSchema.parse(customer.c_balanceResponse);
  // `hasOwn`: the tpaId comes from shopper-writable `c_TPA`.
  if (!tpaId || !Object.hasOwn(entries, tpaId)) return false;
  const entry = balanceEntrySchema.safeParse(entries[tpaId]).data;
  return (
    entry?.totalAvailableBalance !== undefined && entry.hasConsent === true
  );
}

interface TpaBalanceProps extends React.HTMLAttributes<HTMLDivElement> {
  balancePopupText: string;
  useMockData?: boolean;
  mockData?: {
    balance?: {
      tracker?: TrackerData;
      popup?: BalanceData[];
    };
  };
  isEdit?: boolean;
  isPreview?: boolean;
  bdLogo?: BuilderImage;
  /** Hide on tablet/desktop (≥sm). The account-popover placement sets this so
   *  it doesn't double-render with the below-header banner on wider screens. */
  mobileOnly?: boolean;
  /** Forwarded to the tracker — hide the deadline messages under the pills. */
  hideExpiryMessages?: boolean;
  /** The account card sets false: SFRA's `accountCard=true` include omits the
   *  modal wrapper entirely, so no trigger may remain (COM-17). */
  showDetailsModal?: boolean;
  className?: string;
}

export const TpaBalance: React.FC<TpaBalanceProps> = ({
  balancePopupText = "",
  useMockData,
  mockData,
  isEdit,
  isPreview,
  bdLogo,
  mobileOnly,
  hideExpiryMessages,
  showDetailsModal = true,
  className,
  ...rest
}) => {
  const { isLoggedIn } = useAuth();
  const { customer } = useRootLoaderData();
  // Suppress live fetch only when mock data is actually being rendered (editor/preview).
  // A published entry with useMockData=true but outside the editor must still fetch live.
  // Also fetch for an ASIFLEX shopper (a guest, no SLAS login) when the
  // asiflexData cookie is present — the server loader does the real gating.
  const usingEditorMock = Boolean(useMockData && (isEdit || isPreview));
  const shouldFetch = (isLoggedIn || hasAsiflexCookie()) && !usingEditorMock;
  const {
    data: payload,
    isLoading,
    failed,
    reload,
  } = useTpaBalance(shouldFetch);

  // Root loader `customer` is identical on SSR and hydration, so the skeleton
  // is reserved from the first paint only for a shopper the endpoint will
  // report active; anyone else gets nothing to collapse (SFCC-11803).
  const expectsTracker = expectsActiveTracker(customer) && !usingEditorMock;
  const inFlight = isLoading && shouldFetch;

  // No isLoggedIn gate: guest-Mulesoft already yields null (the SCAPI fetch
  // requires auth), so only a real balance — logged-in Mulesoft OR an ASIFLEX
  // cookie (guest or registered) — reaches here. Renders ASIFLEX for guests
  // per SFRA, which has no login check (SFCC-11421).
  const liveBalance =
    !usingEditorMock &&
    !isEdit &&
    payload?.balance &&
    payload.balance.TPA !== ""
      ? payload.balance.balance
      : undefined;

  // Move the tracker's session usage with the cart — only while a live tracker
  // is on screen (each reload is a custom-API call, COM-674). TpaBalance renders
  // inside BasketProvider, so the basket is live here.
  const cartSpend = useCartSpend();
  useTpaBalanceBasketSync({
    enabled: shouldFetch && Boolean(liveBalance?.tracker),
    // The order total, as the tracker's server figure: the minicart figure
    // (`cents`) misses shipping and reward changes (COM-883).
    total: cartSpend.known ? cartSpend.orderCents : undefined,
    reload,
  });

  const [isBalanceModalOpen, setIsBalanceModalOpen] = useState(false);
  // Gates the lazy BalanceModal mount below (COM-189 wave 4B).
  const overlaysReady = useOverlaysReady();

  // Resolve tracker data from mock (Builder preview) or live API
  let trackerData: TrackerData | undefined;
  let popupData: BalanceData[] = [];
  let expiryDays = DEFAULT_EXPIRY_DAYS;

  if (usingEditorMock) {
    trackerData = mockData?.balance?.tracker;
    popupData = mockData?.balance?.popup ?? [];
  } else if (liveBalance && payload) {
    trackerData = liveBalance.tracker;
    popupData = liveBalance.popup ?? [];
    expiryDays = payload.expiryDays;
  }

  // Loading placeholder (SFCC-11803): the MuleSoft-backed balance call runs up
  // to ~25s, and rendering nothing that whole time reads as "no balance".
  // Mirrors the tracker's box model (plus the badge row when the cached plan
  // count says multi-plan) so the header doesn't shift when the real data
  // lands. A re-fetch after an `empty`/`error` result keeps it for an expected
  // tracker; a reload while the tracker IS shown keeps the tracker. The
  // ASIFLEX cookie is client-only, so those fetches get it post-hydration. A
  // settled fetch with nothing to show (`failed` — e.g. a network error on the
  // first fetch after a same-tab re-login) releases it, or it never resolves.
  const showSkeleton =
    !trackerData &&
    (expectsTracker
      ? (!payload && !failed) || inFlight
      : inFlight && hasAsiflexCookie());
  if (showSkeleton) {
    return (
      <div
        {...rest}
        className={cn(mobileOnly && "sm:hidden", className)}
        data-testid="tpa-balance-loading"
      >
        <div className="w-full py-2">
          <div className="mb-1 flex">
            <Skeleton className="w-32 text-lg font-medium">&nbsp;</Skeleton>
          </div>
          <div className="my-1 flex items-center gap-3">
            <Skeleton className="h-3.5 flex-1 rounded-full" />
            <div className="w-24 leading-tight">
              <Skeleton className="font-grotesque font-medium">&nbsp;</Skeleton>
              <Skeleton className="text-xs">&nbsp;</Skeleton>
            </div>
          </div>
          {cachedPlanCount(customer) > 1 && (
            <div
              className="flex gap-2"
              data-testid="tpa-balance-loading-badges"
            >
              <Skeleton className="h-6 w-24 rounded-full" />
              <Skeleton className="h-6 w-24 rounded-full" />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Fetch failure (5xx / timeout / auth failure): show a user-facing message
  // instead of silently rendering nothing (SFCC-10536, parity with SFRA's
  // `balance.error`). Only for shoppers who attempted a live fetch — guests and
  // the Builder editor never reach here, so they can't see a false error.
  if (shouldFetch && !isEdit && payload?.status === "error") {
    return (
      <div
        {...rest}
        className={cn("p-2", mobileOnly && "sm:hidden", className)}
      >
        <p
          data-testid="tpa-balance-error"
          className="font-grotesque text-sm leading-normal text-foreground"
        >
          Balance Information is currently unavailable. Please try again later.
        </p>
      </div>
    );
  }

  if (!trackerData) {
    return null;
  }

  return (
    <div {...rest} className={cn(mobileOnly && "sm:hidden", className)}>
      <Dialog
        open={isBalanceModalOpen}
        onOpenChange={setIsBalanceModalOpen}
        data-testid="tpaBalance-Dialog"
      >
        <div className={cn("w-full", className)}>
          <TpaTracker
            trackerData={trackerData}
            balanceTrackerExpiryDays={expiryDays}
            activeCurrency={ACTIVE_CURRENCY}
            popupData={popupData}
            // Withheld, not just hidden: the tracker wires this to the
            // Details link, the heading and the progress bar alike.
            onDetailsClick={
              showDetailsModal ? () => setIsBalanceModalOpen(true) : undefined
            }
            hideExpiryMessages={hideExpiryMessages}
            data-testid="tpaBalance-TpaTracker"
          />
        </div>
        {/* Closed Radix Dialog content renders nothing, so pre-mounting on the
            ready signal is pure chunk warming; an open click forces the mount
            and the modal shows when the chunk lands. */}
        {(overlaysReady || isBalanceModalOpen) && (
          // OverlayBoundary (PR #1042 blocker 3): a rejected modal chunk
          // degrades to no details-modal instead of unmounting the page.
          <OverlayBoundary overlay="balance-modal" fallback={null}>
            <Suspense fallback={null}>
              <BalanceModal
                balancePopupData={popupData}
                balancePopupText={balancePopupText}
                activeCurrency={ACTIVE_CURRENCY}
                logo={bdLogo}
                data-testid="tpaBalance-BalanceModal"
              />
            </Suspense>
          </OverlayBoundary>
        )}
      </Dialog>
    </div>
  );
};
